import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useUIStore } from './stores/uiStore';
import { authApi } from './services/api/auth';
import { organizationsApi } from './services/api/organizations';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AppLayout } from './components/layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import { CookieBanner } from './components/common/CookieBanner';
import { ToastContainer } from './components/ui/Toast';
import BrowserNotificationPrompt from './components/notifications/BrowserNotificationPrompt';

// Lazy load pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));
const PlaudOAuthCallback = lazy(() => import('./pages/settings/PlaudOAuthCallback'));

// Legal pages
const LegalPage = lazy(() => import('./pages/legal/LegalPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const BoardPage = lazy(() => import('./pages/board/BoardPage'));
const ProjectListPage = lazy(() => import('./pages/projects/ProjectListPage'));
const CreateProjectPage = lazy(() => import('./pages/projects/CreateProjectPage'));
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage'));
const CreateOrganizationPage = lazy(() => import('./pages/onboarding/CreateOrganizationPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Settings pages
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout'));
const ProfilePage = lazy(() => import('./pages/settings/ProfilePage'));
const AccountPage = lazy(() => import('./pages/settings/AccountPage'));
const OrganizationSettingsPage = lazy(() => import('./pages/settings/OrganizationSettingsPage'));
const MembersPage = lazy(() => import('./pages/settings/MembersPage'));
const NotificationsPage = lazy(() => import('./pages/settings/NotificationSettingsPage'));
const VoiceSettingsPage = lazy(() => import('./pages/settings/VoiceSettingsPage'));
const AIPromptsPage = lazy(() => import('./pages/settings/AIPromptsPage'));
const IntegrationsPage = lazy(() => import('./pages/settings/IntegrationsPage'));
const APITokensPage = lazy(() => import('./pages/settings/APITokensPage'));
const WebhooksPage = lazy(() => import('./pages/settings/WebhookSettingsPage'));
const CustomFieldSettings = lazy(() => import('./pages/settings/CustomFieldSettings'));
const MCPSettings = lazy(() => import('./pages/settings/MCPSettings'));

// Analytics pages
const AnalyticsDashboard = lazy(() => import('./pages/analytics/AnalyticsDashboard'));

// Reports pages
const ReportGeneratorPage = lazy(() => import('./pages/reports/ReportGeneratorPage'));

// Meetings pages
const MeetingsPage = lazy(() => import('./pages/meetings/MeetingsPage'));

// Emails pages
const EmailsPage = lazy(() => import('./pages/emails/EmailsPage'));

// Legacy marketplace links lead to the supported integrations settings.

// Search pages
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));

// Subscription pages
const PricingPage = lazy(() => import('./pages/pricing/PricingPage'));
const SubscriptionPage = lazy(() => import('./pages/settings/SubscriptionPage'));
const PlanManagementPage = lazy(() => import('./pages/admin/PlanManagementPage'));

// Enterprise pages
const SSOConfigPage = lazy(() => import('./features/enterprise/SSOConfigPage'));
const MFASetupPage = lazy(() => import('./features/enterprise/MFASetupPage'));
const CustomRolesPage = lazy(() => import('./features/enterprise/CustomRolesPage'));
const AuditLogPage = lazy(() => import('./features/enterprise/AuditLogPage'));
const DataPrivacyPage = lazy(() => import('./features/enterprise/DataPrivacyPage'));

// Super Admin pages
const SuperAdminDashboard = lazy(() => import('./features/enterprise/SuperAdminDashboard'));
const UsersManagementPage = lazy(() => import('./features/enterprise/UsersManagementPage'));
const OrganizationsManagementPage = lazy(() => import('./features/enterprise/OrganizationsManagementPage'));
const UsageOverviewPage = lazy(() => import('./features/enterprise/UsageOverviewPage'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-neutral-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500/20 border-t-primary-600 dark:border-primary-500/20 dark:border-t-primary-400" />
        <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400 animate-pulse-subtle">Loading...</p>
      </div>
    </div>
  );
}

// App initialization - load user data and organizations on startup
function useInitializeAuth(retryNonce: number, onOrgLoadError: (hasError: boolean) => void) {
  const { accessToken, currentOrgId, setUser, setCurrentOrganization, setLoading, logout } = useAuthStore();

  useEffect(() => {
    const initAuth = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        const user = await authApi.getCurrentUser();
        const userData: Parameters<typeof setUser>[0] = {
          id: user.id,
          email: user.email,
          displayName: user.displayName,
          locale: user.locale,
          theme: user.theme,
          timezone: user.timezone,
          isSuperAdmin: user.isSuperAdmin,
        };
        if (user.avatarUrl) {
          userData.avatarUrl = user.avatarUrl;
        }

        // Set user but keep loading state while we validate org
        useAuthStore.setState({
          user: userData,
          isAuthenticated: true,
          // Keep isLoading: true until org validation is complete
        });

        // Always fetch organizations to validate currentOrgId and set currentOrganization
        try {
          const orgs = await organizationsApi.list();
          const firstOrg = orgs[0];
          if (firstOrg) {
            // Validate that currentOrgId is still valid, otherwise use first org
            const validOrg = currentOrgId
              ? orgs.find(org => org.id === currentOrgId)
              : undefined;

            const selectedOrg = validOrg ?? firstOrg;
            // Set both currentOrgId and currentOrganization
            setCurrentOrganization(selectedOrg);
          } else {
            // User has no organizations - clear both
            setCurrentOrganization(null);
          }
          // Org context resolved successfully - clear any prior error
          onOrgLoadError(false);
        } catch (orgErr) {
          // Surface the failure instead of silently entering a broken, org-less
          // session (which otherwise leaves protected routes spinning forever).
          console.error('Failed to fetch organizations:', orgErr);
          onOrgLoadError(true);
        }

        // Now we're done loading
        setLoading(false);
      } catch (err) {
        // Token is invalid or expired
        logout();
      }
    };

    void initAuth();
    // Note: Intentionally not including currentOrgId in deps to avoid re-running on org change.
    // retryNonce is included so a user-triggered retry re-runs the org fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, setUser, setCurrentOrganization, setLoading, logout, onOrgLoadError, retryNonce]);
}

// Theme initialization - apply theme class to document on startup and when theme changes
function useInitializeTheme() {
  const resolvedTheme = useUIStore((state) => state.resolvedTheme);

  useEffect(() => {
    // Apply or remove 'dark' class on document.documentElement
    if (resolvedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [resolvedTheme]);
}

// Blocking screen shown when the startup organization fetch fails, so the app
// never lands in a permanent full-screen spinner with no way out.
function OrgLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-neutral-50 p-4 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950">
      <div className="w-full max-w-md text-center">
        <h2 className="mb-2 text-xl font-semibold text-neutral-900 dark:text-white">
          Couldn&apos;t load your workspace
        </h2>
        <p className="mb-6 text-sm text-neutral-600 dark:text-neutral-400">
          We couldn&apos;t reach the server to load your organizations. Check your connection and try
          again.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={onRetry}
            className="w-full rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
          >
            Retry
          </button>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="w-full rounded-lg px-4 py-2 text-neutral-700 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// Route guard for super-admin-only pages. Members who navigate directly to an
// admin URL are redirected instead of seeing a broken, permission-denied UI.
function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const isSuperAdmin = useAuthStore((state) => state.user?.isSuperAdmin);
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// Detects lazy-chunk load failures (common after a redeploy leaves stale chunks
// referenced by an already-loaded PWA) and reloads once to fetch fresh chunks.
// Guarded by a session flag so a genuinely broken build can't loop forever.
function handleChunkLoadError(error: Error): void {
  const isChunkError =
    error.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      error.message
    );
  if (isChunkError && !sessionStorage.getItem('nk-chunk-reload-attempted')) {
    sessionStorage.setItem('nk-chunk-reload-attempted', '1');
    window.location.reload();
  }
}

export default function App() {
  const [orgLoadError, setOrgLoadError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useInitializeAuth(retryNonce, setOrgLoadError);
  useInitializeTheme();

  const handleRetryOrgLoad = useCallback(() => {
    setOrgLoadError(false);
    useAuthStore.getState().setLoading(true);
    setRetryNonce((n) => n + 1);
  }, []);

  if (orgLoadError) {
    return <OrgLoadError onRetry={handleRetryOrgLoad} />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<PageLoader />}>
          {/* Inner boundary isolates route/lazy-chunk crashes from the whole
              app shell and auto-recovers from stale-chunk load failures. */}
          <ErrorBoundary onError={handleChunkLoadError}>
          <Routes>
          {/* Public landing page */}
          <Route path="/" element={<LandingPage />} />
          {['fr', 'en'].map(lang => (
            <Route key={lang} path={`/${lang}`}>
              <Route index element={<LandingPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="legal-notice" element={<LegalPage kind="notice" />} />
              <Route path="sales-terms" element={<LegalPage kind="sales" />} />
              <Route path="ai-transparency" element={<LegalPage kind="ai" />} />
              <Route path="pricing" element={<PricingPage />} />
            </Route>
          ))}
          <Route path="/legal-notice" element={<LegalPage kind="notice" />} />
          <Route path="/sales-terms" element={<LegalPage kind="sales" />} />
          <Route path="/ai-transparency" element={<LegalPage kind="ai" />} />

          {/* Public auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/oauth/:provider/callback" element={<OAuthCallback />} />
          <Route path="/auth/plaud/callback" element={<PlaudOAuthCallback />} />

          {/* Legal pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Public pricing page */}
          <Route path="/pricing" element={<PricingPage />} />

          {/* Protected routes - no org required */}
          <Route
            path="/onboarding/create-organization"
            element={
              <ProtectedRoute>
                <CreateOrganizationPage />
              </ProtectedRoute>
            }
          />

          {/* Protected routes with AppLayout */}
          <Route
            element={
              <ProtectedRoute requireOrg>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/boards/:boardId" element={<BoardPage />} />

            {/* Project routes */}
            <Route path="/projects" element={<ProjectListPage />} />
            <Route path="/projects/new" element={<CreateProjectPage />} />
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="/projects/:projectId/analytics" element={<AnalyticsDashboard />} />

            {/* Meetings routes */}
            <Route path="/meetings" element={<MeetingsPage />} />
            <Route path="/meetings/:meetingId" element={<MeetingsPage />} />

            {/* Emails routes */}
            <Route path="/emails" element={<EmailsPage />} />
            <Route path="/emails/:emailId" element={<EmailsPage />} />

            {/* Redirect retired extension URLs */}
            <Route path="/plugins" element={<Navigate to="/settings/integrations" replace />} />
            <Route path="/plugins/:pluginId" element={<Navigate to="/settings/integrations" replace />} />

            {/* Search routes */}
            <Route path="/search" element={<SearchResultsPage />} />

            {/* Reports routes */}
            <Route path="/reports" element={<ReportGeneratorPage />} />
            <Route path="/reports/project/:projectId" element={<ReportGeneratorPage />} />

            {/* Legacy Settings routes - redirect to the canonical members page
                inside SettingsLayout (the standalone pages lost settings context) */}
            <Route
              path="/settings/members-list"
              element={<Navigate to="/settings/members" replace />}
            />
            <Route path="/settings/invite" element={<Navigate to="/settings/members" replace />} />

            {/* New Settings routes with layout */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/profile" replace />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="organization" element={<OrganizationSettingsPage />} />
              <Route path="members" element={<MembersPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="voice" element={<VoiceSettingsPage />} />
              <Route path="ai-prompts" element={<AIPromptsPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="api-tokens" element={<APITokensPage />} />
              <Route path="webhooks" element={<WebhooksPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="custom-fields" element={<CustomFieldSettings />} />
              <Route path="mcp" element={<MCPSettings />} />
            </Route>

            {/* Enterprise routes with layout */}
            <Route path="/enterprise" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/enterprise/sso" replace />} />
              <Route path="sso" element={<SSOConfigPage />} />
              <Route path="mfa" element={<MFASetupPage />} />
              <Route path="roles" element={<CustomRolesPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="privacy" element={<DataPrivacyPage />} />
              {/* Super Admin routes - gated so non-admins are redirected away
                  instead of seeing a broken, permission-denied admin UI */}
              <Route
                path="admin"
                element={
                  <RequireSuperAdmin>
                    <SuperAdminDashboard />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="admin/users"
                element={
                  <RequireSuperAdmin>
                    <UsersManagementPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="admin/organizations"
                element={
                  <RequireSuperAdmin>
                    <OrganizationsManagementPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="admin/usage"
                element={
                  <RequireSuperAdmin>
                    <UsageOverviewPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="admin/subscriptions"
                element={
                  <RequireSuperAdmin>
                    <PlanManagementPage />
                  </RequireSuperAdmin>
                }
              />
            </Route>
          </Route>

          {/* Catch all */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ErrorBoundary>
        </Suspense>

        {/* Cookie consent banner */}
        <CookieBanner />

        {/* Toast notifications */}
        <ToastContainer />

        {/* Browser notification permission prompt */}
        <BrowserNotificationPrompt />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
